import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../../usermgmt/src/lib/shared/environments/environment.dev';
import { v4 as uuidv4 } from 'uuid';
import { ToasterService } from './toaster.service';

export interface SSEMessage<T = any> {
  id?: string;
  event?: string;
  data: T;
}

@Injectable({
  providedIn: 'root',
})
export class SseService {
  private baseUrl = environment.urlMapping.SSE_URL;
  private connectionId: string = '';
  private controllers: Map<string, AbortController> = new Map();

  constructor(private ngZone: NgZone, private toasterService: ToasterService) {}

  /**
   * Create an SSE connection using Fetch API to support custom headers
   * and return an Observable that emits messages.
   * @param endpoint The API endpoint for the SSE stream
   * @param queryParams Optional query parameters
   * @returns Observable that emits SSE messages
   */
  connect<T = any>(endpoint: string, queryParams?: Record<string, any>): Observable<SSEMessage<T>> {
    return this.connectWithEvents(endpoint, [], queryParams);
  }

  /**
   * Create an SSE connection with support for named events using Fetch API.
   * @param endpoint The API endpoint for the SSE stream
   * @param eventNames Array of event names to listen for (optional filtering)
   * @param queryParams Optional query parameters
   * @returns Observable that emits SSE messages with event names
   */
  connectWithEvents<T = any>(
    endpoint: string,
    eventNames: string[] = [],
    queryParams?: Record<string, any>
  ): Observable<SSEMessage<T>> {
    const url = this.buildUrl(endpoint, queryParams);
    
    return new Observable<SSEMessage<T>>((observer) => {
      const controller = new AbortController();
      this.controllers.set(endpoint, controller);

      const headers = {
        'Accept': 'text/event-stream',
        'ngrok-skip-browser-warning': 'true',
      };

      fetch(url, {
        method: 'GET',
        headers: headers,
        signal: controller.signal
      }).then(async (response) => {
        if (!response.ok) {
          throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
        }
        
        if (!response.body) {
          throw new Error('ReadableStream not supported in this browser.');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split(/\r\n|\r|\n/);
            // Save the last partial line
            buffer = lines.pop() || '';
            
            let currentId: string | null = null;
            let currentEvent: string | null = null;
            let currentData: string = '';

            for (const line of lines) {
              if (line.trim() === '') {
                // End of event block, emit if we have data
                if (currentData) {
                   const message: SSEMessage<T> = { data: this.tryParseJSON(currentData) };
                   if (currentId) message.id = currentId;
                   if (currentEvent) message.event = currentEvent;

                   this.ngZone.run(() => {
                     // Filter by event name if specified
                     if (eventNames.length === 0 || (message.event && eventNames.includes(message.event))) {
                        observer.next(message);
                     } else if (eventNames.length === 0 && !message.event) {
                        // Default 'message' event usually has no event field or 'message'
                        observer.next(message);
                     }
                   });
                }
                currentId = null;
                currentEvent = null;
                currentData = '';
                continue;
              }

              if (line.startsWith('id:')) {
                currentId = line.substring(3).trim();
              } else if (line.startsWith('event:')) {
                currentEvent = line.substring(6).trim();
              } else if (line.startsWith('data:')) {
                currentData += line.substring(5).trim();
              }
            }


          }
        } catch (error: any) {
          if (error.name !== 'AbortError') {
             this.ngZone.run(() => observer.error(error));
          }
        } finally {
           this.ngZone.run(() => observer.complete());
           this.controllers.delete(endpoint);
        }
      }).catch((error) => {
         if (error.name !== 'AbortError') {
            this.ngZone.run(() => observer.error(error));
         }
      });

      // Cleanup function
      return () => {
        controller.abort();
        this.controllers.delete(endpoint);
      };
    });
  }

  private tryParseJSON(str: string): any {
    try {
      return JSON.parse(str);
    } catch (e) {
      return str;
    }
  }

  /**
   * Close a specific SSE connection by endpoint.
   * @param endpoint The endpoint to close
   */
  disconnect(endpoint: string): void {
    const controller = this.controllers.get(endpoint);
    if (controller) {
      controller.abort();
      this.controllers.delete(endpoint);
    }
  }

  /**
   * Close all active SSE connections.
   */
  disconnectAll(): void {
    this.controllers.forEach((controller) => {
      controller.abort();
    });
    this.controllers.clear();
  }

  /**
   * Check if a specific SSE connection is active.
   * @param endpoint The endpoint to check
   * @returns true if the connection is active
   */
  isConnected(endpoint: string): boolean {
    return this.controllers.has(endpoint);
  }

  /**
   * Build the full URL with query parameters.
   */
  private buildUrl(endpoint: string, queryParams?: Record<string, any>): string {
    let url = `${this.baseUrl}/${endpoint}`;
    
    if (queryParams) {
      const params = new URLSearchParams();
      for (const key in queryParams) {
        if (queryParams.hasOwnProperty(key) && queryParams[key] !== undefined && queryParams[key] !== null) {
          params.append(key, String(queryParams[key]));
        }
      }
      const paramString = params.toString();
      if (paramString) {
        url += `?${paramString}`;
      }
    }
    
    return url;
  }

  /**
   * Generates a new random UUID for the SSE connection.
   * This should be called on login or session start.
   */
  generateConnectionId(): string {
    this.connectionId = uuidv4();
    return this.connectionId;
  }

  /**
   * Retrieves the current connection ID.
   * If one doesn't exist, it generates a new one.
   */
  getConnectionId(): string {
    // if (!this.connectionId) {
      this.connectionId = uuidv4();
    // }
    return this.connectionId;
  }
}
